import React, { useState, useEffect } from 'react'
import SpaceTheme from '../components/SpaceTheme'
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  CircularProgress,
  Button
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Description as DescriptionIcon,
  Person as PersonIcon,
  Class as ClassIcon
} from '@mui/icons-material'
import { useAuth } from '../hooks/useAuth'
import { auth, db } from '../firebase/config'
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore'
import toast from 'react-hot-toast'

const StudentDocuments = ({ onBack }) => {
  const { user } = useAuth()
  const [documents, setDocuments] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (user && user.uid) {
      loadDocuments()
    }
  }, [user])

  const loadDocuments = async () => {
    try {
      setLoading(true)
      console.log('🔍 Gelen dökümanlar yükleniyor...')
      console.log('👤 Kullanıcı ID:', user.uid)
      
      // Öğrencinin dökümanlarını al
      const documentsRef = collection(db, 'sentDocuments')
      const documentsQuery = query(
        documentsRef,
        where('studentIds', 'array-contains', user.uid)
      )
      
      const documentsSnapshot = await getDocs(documentsQuery)
      console.log('📝 Bulunan döküman sayısı:', documentsSnapshot.size)
      
      const documentsData = []
      
      for (const documentDoc of documentsSnapshot.docs) {
        const documentData = documentDoc.data()
        console.log('🔍 Döküman verisi:', documentData)
        
        // Öğretmen adını al
        let teacherName = 'Bilinmeyen Öğretmen'
        if (documentData.teacherId) {
          try {
            const teacherDoc = await getDoc(doc(db, 'users', documentData.teacherId))
            if (teacherDoc.exists()) {
              teacherName = teacherDoc.data().username || teacherDoc.data().email || 'Bilinmeyen Öğretmen'
            }
          } catch (error) {
            console.error('Error loading teacher name:', error)
          }
        }
        
        // Döküman türünü belirle
        let documentType = 'Sınıf Dökümanı'
        if (documentData.studentIds && documentData.studentIds.length === 1 && documentData.studentIds.includes(user.uid)) {
          documentType = 'Bireysel'
        }
        
        documentsData.push({
          id: documentDoc.id,
          documentName: documentData.documentName || 'İsimsiz Döküman',
          documentUrl: documentData.documentUrl || '',
          teacherName,
          className: documentData.className || '',
          documentType,
          sentDate: documentData.sentDate?.toDate() || new Date()
        })
      }
      
      console.log('📋 Yüklenen dökümanlar:', documentsData)
      
      // Manuel sıralama (en yeni üstte)
      documentsData.sort((a, b) => b.sentDate - a.sentDate)
      
      setDocuments(documentsData)
      
    } catch (error) {
      console.error('Error loading documents:', error)
      toast.error('Dökümanlar yüklenirken hata oluştu')
    } finally {
      setLoading(false)
    }
  }

  const getDocumentTypeColor = (type) => {
    switch (type) {
      case 'Bireysel':
        return 'success'
      case 'Sınıf Dökümanı':
        return 'primary'
      default:
        return 'default'
    }
  }

  const formatDate = (date) => {
    return new Intl.DateTimeFormat('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date)
  }

  const handleDocumentClick = (document) => {
    if (document.documentUrl) {
      // Dökümanı yeni sekmede aç
      window.open(document.documentUrl, '_blank')
    } else {
      toast.error('Döküman URL\'si bulunamadı')
    }
  }



  return (
    <SpaceTheme>
      <Box sx={{ flexGrow: 1 }}>
      {/* App Bar */}
      <AppBar position="static">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onBack}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Gelen Dökümanlar
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="md" sx={{ py: 3 }}>
        {!user || !user.uid ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography>Kullanıcı bilgileri yükleniyor...</Typography>
          </Box>
        ) : loading ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>Dökümanlar yükleniyor...</Typography>
          </Box>
        ) : documents.length === 0 ? (
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <DescriptionIcon sx={{ fontSize: 60, color: '#6E6D6D', mb: 2 }} />
              <Typography variant="h6" color="#6E6D6D" gutterBottom>
                Henüz döküman almadınız
              </Typography>
              <Typography variant="body2" color="#6E6D6D">
                Öğretmenlerinizden döküman aldığınızda burada görünecek
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <List>
            {documents.map((document, index) => (
              <Card
                key={document.id}
                sx={{
                  mb: 2,
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                    transform: 'translateY(-2px)',
                    transition: 'all 0.2s ease-in-out',
                    boxShadow: 3
                  }
                }}
                onClick={() => handleDocumentClick(document)}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    {/* Döküman İkonu */}
                    <Avatar
                      sx={{ 
                        width: 60, 
                        height: 60, 
                        flexShrink: 0,
                        backgroundColor: 'primary.main'
                      }}
                    >
                      <DescriptionIcon sx={{ fontSize: 32, color: 'white' }} />
                    </Avatar>
                    
                    {/* Döküman İçeriği */}
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Chip
                          label={document.documentType}
                          color={getDocumentTypeColor(document.documentType)}
                          size="small"
                        />
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(document.sentDate)}
                        </Typography>
                      </Box>
                      
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 'bold',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 1,
                          WebkitBoxOrient: 'vertical',
                          mb: 1
                        }}
                      >
                        {document.documentName}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            {document.teacherName}
                          </Typography>
                        </Box>
                        
                        {document.className && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <ClassIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                              {document.className}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                      
                      
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </List>
        )}
      </Container>
      </Box>
    </SpaceTheme>
  )
}

export default StudentDocuments
